import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  ChartLine,
  Users,
  UserPlus,
  TrendUp,
  ArrowLeft,
  Download,
  ArrowsClockwise,
  CalendarBlank,
  ChartBar,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

// Chart component using simple SVG
const AdoptionChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-slate-400">
        No data available for chart
      </div>
    );
  }

  const maxDau = Math.max(...data.map(d => d.dau || 0), 1);
  const maxTotal = Math.max(...data.map(d => d.total_users_to_date || 0), 1);
  
  const width = 100;
  const height = 200;
  const padding = { top: 20, right: 10, bottom: 30, left: 10 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  const getX = (index) => padding.left + (index / (data.length - 1 || 1)) * chartWidth;
  const getYDau = (value) => padding.top + chartHeight - (value / maxDau) * chartHeight;
  const getYTotal = (value) => padding.top + chartHeight - (value / maxTotal) * chartHeight;

  const dauPoints = data.map((d, i) => `${getX(i)},${getYDau(d.dau || 0)}`).join(' ');
  const totalPoints = data.map((d, i) => `${getX(i)},${getYTotal(d.total_users_to_date || 0)}`).join(' ');

  return (
    <div className="relative w-full h-64">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full" preserveAspectRatio="none">
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => (
          <line
            key={i}
            x1={padding.left}
            y1={padding.top + chartHeight * ratio}
            x2={width - padding.right}
            y2={padding.top + chartHeight * ratio}
            stroke="#e2e8f0"
            strokeWidth="0.5"
          />
        ))}
        
        {/* DAU line (primary) */}
        <polyline
          points={dauPoints}
          fill="none"
          stroke="#6366f1"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        
        {/* Total users line (secondary) */}
        <polyline
          points={totalPoints}
          fill="none"
          stroke="#10b981"
          strokeWidth="1.5"
          strokeDasharray="4,2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        
        {/* Data points for DAU */}
        {data.map((d, i) => (
          <circle
            key={i}
            cx={getX(i)}
            cy={getYDau(d.dau || 0)}
            r="3"
            fill="#6366f1"
            className="hover:r-4 transition-all"
          />
        ))}
      </svg>
      
      {/* Legend */}
      <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-6 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-0.5 bg-indigo-500 rounded"></div>
          <span className="text-slate-600">DAU</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-0.5 bg-emerald-500 rounded" style={{background: 'repeating-linear-gradient(90deg, #10b981 0, #10b981 4px, transparent 4px, transparent 6px)'}}></div>
          <span className="text-slate-600">Total Users</span>
        </div>
      </div>
    </div>
  );
};

export const AdoptionAnalyticsPage = () => {
  const { department = 'substation' } = useParams();
  const { api, isSupervisorPlus } = useAuth();
  const navigate = useNavigate();
  
  const [rollups, setRollups] = useState([]);
  const [summary, setSummary] = useState(null);
  const [todayData, setTodayData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  // Date filters
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  const fetchData = useCallback(async () => {
    if (!isSupervisorPlus()) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      // Fetch rollups
      const rollupsRes = await api.get(`/adoption/${department}?start=${startDate}&end=${endDate}`);
      setRollups(rollupsRes.data?.rollups || []);
      setSummary(rollupsRes.data?.summary || null);
      
      // Fetch today's data
      const todayRes = await api.get(`/adoption/${department}/today`);
      setTodayData(todayRes.data);
    } catch (error) {
      console.error('Failed to fetch adoption data:', error);
      toast.error('Failed to load adoption data');
    } finally {
      setLoading(false);
    }
  }, [api, department, startDate, endDate, isSupervisorPlus]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api.get(`/adoption/${department}/export?start=${startDate}&end=${endDate}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `adoption_${department}_${startDate}_${endDate}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleRunRollup = async () => {
    try {
      await api.post(`/adoption/${department}/run-rollup`);
      toast.success('Rollup completed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Rollup failed');
    }
  };

  if (!isSupervisorPlus()) {
    return (
      <div className="p-6 text-center">
        <p className="text-slate-500">Supervisor access required to view adoption analytics.</p>
      </div>
    );
  }

  // Prepare chart data (reverse for chronological order)
  const chartData = [...rollups].reverse();

  return (
    <div className="p-6 lg:p-10 animate-fade-in" data-testid="adoption-analytics-page">
      {/* Header */}
      <div className="mb-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/dashboard')}
          className="mb-4 -ml-2"
        >
          <ArrowLeft size={18} className="mr-2" />
          Back to Hub
        </Button>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2 capitalize">
              <ChartLine size={28} weight="fill" className="text-indigo-600" />
              {department} Adoption Analytics
            </h1>
            <p className="text-slate-600 mt-1">
              Track user engagement and adoption metrics
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleRunRollup}>
              <ArrowsClockwise size={16} className="mr-2" />
              Run Rollup
            </Button>
            <Button onClick={handleExport} disabled={exporting}>
              <Download size={16} className="mr-2" />
              {exporting ? 'Exporting...' : 'Export CSV'}
            </Button>
          </div>
        </div>
      </div>

      {/* Today's Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-indigo-200 bg-indigo-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-indigo-500 rounded-lg flex items-center justify-center">
                <Users size={24} className="text-white" />
              </div>
              <div>
                <p className="text-sm text-indigo-600 font-medium">Today's DAU</p>
                <p className="text-3xl font-bold text-indigo-900">
                  {loading ? '-' : todayData?.dau || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-emerald-500 rounded-lg flex items-center justify-center">
                <UserPlus size={24} className="text-white" />
              </div>
              <div>
                <p className="text-sm text-emerald-600 font-medium">New Today</p>
                <p className="text-3xl font-bold text-emerald-900">
                  {loading ? '-' : todayData?.new_users || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-purple-200 bg-purple-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
                <TrendUp size={24} className="text-white" />
              </div>
              <div>
                <p className="text-sm text-purple-600 font-medium">Total Users</p>
                <p className="text-3xl font-bold text-purple-900">
                  {loading ? '-' : todayData?.total_users_to_date || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center">
                <ChartBar size={24} className="text-white" />
              </div>
              <div>
                <p className="text-sm text-amber-600 font-medium">Avg DAU (Range)</p>
                <p className="text-3xl font-bold text-amber-900">
                  {loading ? '-' : summary?.avg_dau || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="text-sm font-medium text-slate-600 mb-1 block">Start Date</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-40"
                data-testid="start-date-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600 mb-1 block">End Date</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-40"
                data-testid="end-date-input"
              />
            </div>
            <Button variant="outline" onClick={fetchData} disabled={loading}>
              <ArrowsClockwise size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Chart */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ChartLine size={20} className="text-indigo-600" />
            DAU Trend
          </CardTitle>
          <CardDescription>
            Daily active users over selected date range ({rollups.length} days)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-64 flex items-center justify-center">
              <ArrowsClockwise size={32} className="animate-spin text-slate-400" />
            </div>
          ) : (
            <AdoptionChart data={chartData} />
          )}
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardHeader>
          <CardTitle>Daily Rollups</CardTitle>
          <CardDescription>
            Detailed daily adoption metrics
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <ArrowsClockwise size={32} className="animate-spin text-slate-400" />
            </div>
          ) : rollups.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <ChartLine size={48} className="mx-auto mb-4 text-slate-300" />
              <p>No rollup data available for this date range</p>
              <p className="text-sm mt-1">Try running a rollup or adjusting the date range</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">DAU</TableHead>
                  <TableHead className="text-right">New Users</TableHead>
                  <TableHead className="text-right">Total Users</TableHead>
                  <TableHead className="text-right">WAU</TableHead>
                  <TableHead className="text-right">MAU</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rollups.map((rollup, index) => (
                  <TableRow key={rollup.rollup_date} data-testid={`rollup-row-${index}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <CalendarBlank size={16} className="text-slate-400" />
                        {new Date(rollup.rollup_date).toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline" className="bg-indigo-50 text-indigo-700">
                        {rollup.dau}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {rollup.new_users > 0 ? (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          +{rollup.new_users}
                        </Badge>
                      ) : (
                        <span className="text-slate-400">0</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {rollup.total_users_to_date}
                    </TableCell>
                    <TableCell className="text-right text-slate-600">
                      {rollup.wau || '-'}
                    </TableCell>
                    <TableCell className="text-right text-slate-600">
                      {rollup.mau || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdoptionAnalyticsPage;
